import { Injectable } from '@angular/core'
import { BehaviorSubject, finalize, Observable } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class LoaderService {
  // Track all active loader IDs
  private activeLoaders: Set<string> = new Set()

  // Track individual loader states with default value `true` for uninitialized loaders
  private loaderStates: Map<string, BehaviorSubject<boolean>> = new Map()

  // Aggregate loading state
  private isLoadingSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true) // Initially true

  /**
   * Observable that emits `true` if any loader is active, else `false`.
   */
  get isLoading$(): Observable<boolean> {
    return this.isLoadingSubject.asObservable()
  }

  /**
   * Shows the loader for a specific identifier.
   * @param id Unique identifier for the loader.
   */
  showLoader(id: string): void {
    if (!this.loaderStates.has(id)) {
      // Initialize loader state as loading
      this.loaderStates.set(id, new BehaviorSubject<boolean>(true))
    }
    this.activeLoaders.add(id)
    this.loaderStates.get(id)?.next(true)
    this.updateLoadingState()
  }

  /**
   * Hides the loader for a specific identifier.
   * @param id Unique identifier for the loader.
   */
  hideLoader(id: string): void {
    this.activeLoaders.delete(id)
    if (this.loaderStates.has(id)) {
      this.loaderStates.get(id)?.next(false)
    }
    this.updateLoadingState()
  }

  /**
   * Retrieves the loading state observable for a specific loader ID.
   * If the loader is not initialized, it is considered to be loading (`true`) by default.
   * @param id The unique identifier for the loader.
   * @returns An Observable<boolean> indicating if the loader is active.
   */
  getLoaderState(id: string): Observable<boolean> {
    if (!this.loaderStates.has(id)) {
      // Initialize loader state as loading if not already initialized
      this.loaderStates.set(id, new BehaviorSubject<boolean>(true))
      this.activeLoaders.add(id)
      this.updateLoadingState()
    }
    return this.loaderStates.get(id)!.asObservable()
  }

  /**
   * Updates the aggregate loading state based on active loaders.
   */
  private updateLoadingState(): void {
    this.isLoadingSubject.next(this.activeLoaders.size > 0)
  }

  /**
   * Wraps an observable operation with loader and error handling.
   * @param operation A function that returns an observable.
   * @param loaderId A unique identifier for the loader.
   * @returns The observable with loader and error handling.
   */
  withLoader<T>(
    operation: () => Observable<T>,
    loaderId: string
  ): Observable<T> {
    this.showLoader(loaderId)
    return operation().pipe(finalize(() => this.hideLoader(loaderId)))
  }
}
